import { merge } from 'lodash';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import TypeDefinitionKey from '../types/metadataDefinition.type';
import MetadataFields from '../types/metadataFields.type';
import getInitialValueByMetatype from '../utils/getInitialValueByMetatype';

const metadataTypes: TypeDefinitionKey[] = [
  'boolean',
  'color',
  'date',
  'dimension',
  'integer',
  'json',
  'money',
  'multi_line_text_field',
  'numeric',
  'rating',
  'rich_text_field',
  'single_line_text_field',
  'string_integer',
  'timestamp',
  'timezone',
  'url',
  'volume',
  'weight',
  'tax_definition',
  'multi_currency_money',
];

type Args = {
  initialValue?: unknown;
};

const useMetadataField = ({ initialValue }: Args) => {
  const [fields, setFields] = useState<MetadataFields>(
    metadataTypes.reduce(
      (acc, curr) => ({
        ...acc,
        [curr]: getInitialValueByMetatype(curr),
      }),
      {} as MetadataFields,
    ),
  );

  const onFieldChange = (details: {
    value: unknown;
    field: keyof typeof fields;
  }) => {
    setFields((prevFields) => {
      const fieldValue = prevFields[details.field];

      if (Array.isArray(fieldValue)) {
        const detailsValue = details.value as Record<string, unknown>[];

        const mergedArray = fieldValue.map(
          (item) =>
            detailsValue.find((newItem) => newItem.id === item.id) || item,
        );

        const newItems = detailsValue.filter(
          (newItem) => !fieldValue.some((item) => item.id === newItem.id),
        );

        return merge({}, prevFields, {
          [details.field]: [...mergedArray, ...newItems],
        });
      }

      return merge({}, prevFields, { [details.field]: details.value });
    });
  };

  const handleAddMultiCurrencyMoneyRecord = () => {
    setFields((prevFields) => ({
      ...prevFields,
      multi_currency_money: [
        ...prevFields.multi_currency_money,
        {
          id: uuid(),
          value: '0',
          currency_code: 'CAD',
        },
      ],
    }));
  };

  const handleRemoveMultiCurrencyMoneyRecord = (
    id: string,
    metadata_key: string,
    getFieldValue: (value: Record<string, unknown>) => void,
  ) => {
    const updatedFields = {
      ...fields,
      multi_currency_money: fields.multi_currency_money.filter(
        (item) => item.id !== id,
      ),
    };

    setFields(updatedFields);
    getFieldValue({
      [metadata_key]: fields.multi_currency_money.map((item) => ({
        currency_code: item.currency_code,
        value: parseFloat(item.value).toFixed(2),
      })),
    });
  };

  const handleAddMultiTaxDefinitionRecord = () => {
    setFields((prevFields) => ({
      ...prevFields,
      tax_definition: [
        ...prevFields.tax_definition,
        {
          id: uuid(),
          tax_code: 'GST',
          multiplier: '0.5',
        },
      ],
    }));
  };

  const handleRemoveMultiTaxDefinitionRecord = (
    id: string,
    metadata_key: string,
    getFieldValue: (value: Record<string, unknown>) => void,
  ) => {
    const updatedFields = {
      ...fields,
      tax_definition: fields.tax_definition.filter((item) => item.id !== id),
    };

    setFields(updatedFields);
    getFieldValue({
      [metadata_key]: fields.tax_definition.map((item) => ({
        tax_code: item.tax_code,
        multiplier: parseFloat(item.multiplier).toFixed(2),
      })),
    });
  };

  useEffect(() => {
    if (initialValue) {
      setFields((prev) => ({
        ...prev,
        ...metadataTypes.reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: getInitialValueByMetatype(curr, initialValue),
          }),
          {},
        ),
      }));
    }
  }, [initialValue]);

  return {
    fields,
    onFieldChange,
    handleAddMultiCurrencyMoneyRecord,
    handleRemoveMultiCurrencyMoneyRecord,
    handleAddMultiTaxDefinitionRecord,
    handleRemoveMultiTaxDefinitionRecord,
  };
};

export default useMetadataField;
